import { types, flow } from 'mobx-state-tree';
import sortBy from 'lodash/sortBy';
import { getDay } from 'date-fns';

import { Distributor } from 'models/types';
import { DistributorSchedules } from './types/distributor';
import { getRootStore } from 'models/root';
import api from 'services/API';
import dateUtilities from 'utils/dateUtilities';
import i18n from 'i18next';

export const distributorsInitialState = {
  all: [],
  selectedDistributorIds: [],
  distributorsSchedules: [],
  activeScheduleIndex: [],
  coverage: 7,
  state: 'done',
  updated: null,
  currentDistributor: null,
  customCoverage: [],
};

export const distributorsModel = types
  .model({
    all: types.array(Distributor),
    selectedDistributorIds: types.array(types.union(types.number, types.null)),
    distributorsSchedules: types.array(DistributorSchedules),
    activeScheduleIndex: types.array(
      types.model({
        distributorId: types.number,
        scheduleIndex: types.number,
      }),
    ),
    coverage: types.maybeNull(types.number),
    state: types.enumeration('state', ['done', 'pending', 'error']),
    updated: types.maybeNull(types.Date),
    currentDistributor: types.maybeNull(types.reference(Distributor)),
    customCoverage: types.array(
      types.model({
        distributorId: types.number,
        coverage: types.number,
      }),
    ),
  })
  .views(self => ({
    get availableDistributors() {
      return self.all.slice();
    },

    get filteredDistributors() {
      if (self.selectedDistributorIds.length) {
        return self.availableDistributors.filter(distributor =>
          self.selectedDistributorIds.includes(distributor.id),
        );
      } else {
        return self.availableDistributors;
      }
    },

    getDistributorById(id) {
      return self.availableDistributors.find(distributor => distributor.id === id);
    },

    getDistributorSchedulesByDistributorId(distributorId) {
      const schedules = self.distributorsSchedules
        .filter(schedule => schedule.distributor_id === distributorId)
        .map(schedule => {
          const today = getDay(new Date());
          const orderDay = dateUtilities.getDayWeekNumber(schedule.order_day_of_week);
          const deliveryDay = dateUtilities.getDeliveryDayByFrequency(
            schedule.order_day_of_week,
            schedule.delivery_frequency_days,
          );

          let days_to_order = 0;
          if (orderDay < today) {
            days_to_order = orderDay - today + 7;
          } else {
            days_to_order = orderDay - today;
          }

          let days_to_delivery = 0;
          const deliveryDayNumber = dateUtilities.getDayWeekNumber(deliveryDay);
          if (deliveryDayNumber < today) {
            days_to_delivery = deliveryDayNumber - today + 7;
          } else {
            days_to_delivery = deliveryDayNumber - today;
          }
          return {
            ...schedule,
            days_to_delivery,
            days_to_order,
          };
        });
      return sortBy(schedules, 'days_to_delivery');
    },

    getCustomCoverageByDistributorId(distributorId) {
      return self.customCoverage.find(item => item.distributorId === distributorId)?.coverage;
    },
  }))
  .actions(self => ({
    createDistributor: flow(function* (distributor) {
      try {
        self.state = 'pending';
        const response = yield api.createDistributor(distributor);
        const responseRow = response?.data?.row;
        if (responseRow) {
          self.all.push(responseRow);
          self.state = 'done';
          self.updated = new Date();
          self.setCurrentDistributor(responseRow);
          self.setActiveScheduleIndex(responseRow.id, 0);
          return responseRow;
        } else {
          self.state = 'done';
          self.updated = new Date();
        }
      } catch (error) {
        self.state = 'error';
        self.updated = new Date();
        console.error(error);
        return Promise.reject(error);
      }
    }),
    createSchedules: flow(function* (schedules) {
      try {
        const response = yield api.createDeliverySchedules(schedules);
        if (response?.data?.row) {
          self.patchSchedule(response.data.row);
        }
        return response.data.row;
      } catch (error) {
        console.error(error);
        return Promise.reject(error);
      } finally {
        self.state = 'done';
        self.updated = new Date();
      }
    }),
    updateSchedules: flow(function* (id, payload) {
      try {
        const response = yield api.patchDeliverySchedules(id, payload);
        if (response?.data?.row) {
          self.patchSchedule(response.data.row);
        }
        return response.data.row;
      } catch (error) {
        console.error(error);
        return Promise.reject(error);
      } finally {
        self.state = 'done';
        self.updated = new Date();
      }
    }),
    deleteSchedule: flow(function* (id) {
      try {
        const response = yield api.deleteDeliverySchedule(id);
        if (response) {
          self.distributorsSchedules.replace(
            self.distributorsSchedules.filter(schedule => schedule.id !== id),
          );
        }
        return response;
      } catch (err) {
        console.error(i18n.t('models.distributorsStore.failedToDeleteSchedule'), err);
        return Promise.reject(err);
      }
    }),
    deleteDistributor: flow(function* (id) {
      const root = getRootStore();

      try {
        const response = yield api.deleteDistributor(id);
        if (response) {
          self.currentDistributor = null;
          self.all.replace(self.all.filter(schedule => schedule.id !== id));

          const updatedSKUs = root.skuStore.all
            .filter(sku => sku.distributor_id === id)
            .map(sku => ({ ...sku, distributor_id: null }));

          updatedSKUs.forEach(root.skuStore.handleSkuUpdate);
        }
        return response;
      } catch (err) {
        console.error(i18n.t('models.distributorsStore.failedToDeleteDistributor'), err);
        return Promise.reject(err);
      }
    }),
    setSelectedDistributorIds(ids) {
      self.selectedDistributorIds.replace(ids);
    },
    setDistributors(distributors) {
      self.all.replace(distributors);
      self.activeScheduleIndex.replace(
        distributors.map(distributor => ({
          distributorId: distributor.id,
          scheduleIndex: 0,
        })),
      );
    },
    setDistributorsSchedules(schedules) {
      self.distributorsSchedules.replace(schedules);
    },

    setActiveScheduleIndex(distributorId, scheduleIndex) {
      self.activeScheduleIndex.replace(
        self.activeScheduleIndex.filter(item => item.distributorId !== distributorId),
      );
      self.activeScheduleIndex.push({ distributorId, scheduleIndex });
    },
    setCoverage(coverage) {
      self.coverage = coverage;
    },
    setCustomCoverage(distributorId, coverage) {
      self.customCoverage.replace(
        self.customCoverage.filter(item => item.distributorId !== distributorId),
      );
      self.customCoverage.push({ distributorId, coverage });
    },
    resetCustomCoverage() {
      self.customCoverage.replace([]);
    },
    addDistributor(distributor) {
      self.all.push(distributor);
      self.setSelectedDistributorIds([...self.selectedDistributorIds, distributor.id]);
    },
    patchDistributor(distributor) {
      const index = self.all.findIndex(d => d.id === distributor.id);
      if (index !== -1) {
        self.all[index] = { ...distributor };
      } else {
        self.all.push({ ...distributor });
      }
    },
    setCurrentDistributor(distributor) {
      self.currentDistributor = distributor === null ? null : distributor?.id;
    },
    patchSchedule(schedule) {
      const index = self.distributorsSchedules.findIndex(s => s.id === schedule.id);
      if (index !== -1) {
        self.distributorsSchedules[index] = {
          ...self.distributorsSchedules[index],
          ...schedule,
        };
      } else {
        self.distributorsSchedules.push(schedule);
      }
    },
  }));
